import axios from "axios";
import { supabase } from "../../config/supabaseClient";
import * as XLSX from "xlsx";
import { trainingFormStatus, trainingFormStatusLabels, TRAINING_CATEGORY_OPTIONS } from "../constants";
import moment from "moment";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { messages } from "../alertMessages";
import { isUnemployedEmploymentType } from "./employmentTypeUtils";
import { hasUnemployedTypesConfigured } from "./dashboardUnemployedUtils";

/**
 * Soft deletes records by updating their status to 'archived'
 * @param {Object} supabase
 * @param {string} tableName
 * @param {string} conditionField
 * @param {string} conditionValue
 * @returns {Promise<{success: boolean, error: string|null}>}
 */
export async function archiveRecords(
  supabase,
  tableName,
  conditionField,
  conditionValue
) {
  try {
    const { error } = await supabase
      .from(tableName)
      .update({
        status: "archived",
        updated_at: new Date().toISOString(),
      })
      .eq(conditionField, conditionValue);

    if (error) {
      return {
        success: false,
        error: error.message,
      };
    }

    return {
      success: true,
      error: null,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
}

export function capitalizeFirstCharacter(str) {
  if (str.length === 0) return str; // Return the empty string if the input is empty
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];

// Date/time utilities have been moved to datetimeUtils.js
// Import from: import { formatDateForDisplay, parseExcelDate, ... } from "../../constant/utils/datetimeUtils";

export const isEnvTrue = (key) => {
  const val = process.env[key];
  return val?.trim().toLowerCase() === "true";
};

export async function sendInvite(userData, email, role) {
  try {
    if (!email || typeof email !== "string") {
      return { success: false, error: "Invalid email format" };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { success: false, error: "Invalid email format" };
    }
    // Code need to be changed to invite user with default password and email confirmation
    // First, try to find the user by email
    // Fetch user(s) from authentication table by email or uuid
    let users, listError;
    if (userData?.user_id) {
      // If user_id (uuid) is provided, fetch by uuid
      const { data, error } = await supabase.auth.admin.getUserById(
        userData.user_id
      );
      users = { users: data ? [data.user] : [] };
      listError = error;
    }

    const user = users?.users?.[0];
    if (user) {
      // User exists, check if email is confirmed
      if (!user?.email_confirmed_at) {
        // Email not verified, send invite link
        // If the email is not verified, try to delete the user from auth table and then send invite again
        const { error: deleteError } = await supabase.auth.admin.deleteUser(
          userData.user_id
        );
        if (deleteError) {
          return { success: false, error: messages.INVITE_SENT_FAILED };
        }
        // After deletion, send invite again
        const { data, error } = await supabase.auth.admin.inviteUserByEmail(
          email,
          {
            // redirectTo: `${process.env.REACT_APP_URL}/reset-password`,
            data: { role },
          }
        );
        if (error) {
          return { success: false, error: messages.INVITE_SENT_FAILED };
        }

        const { error: updateError } = await supabase
          .from("company_user")
          .update({
            user_id: data.user.id,
          })
          .eq("id", userData.id);

        if (updateError) {
          return { success: false, error: updateError.message || updateError };
        }
        return {
          success: true,
          userId: data.user.id,
          message: messages.INVITE_SENT_SUCCESSFULLY,
        };
      } else {
        // Email verified, send password reset link
        const res = await resendPasswordReset(email);
        if(!res.success){
          return { success: false, error: res.error || messages.INVITE_SENT_FAILED };
        }
        return {
          success: true,
          message: messages.INVITE_SENT_SUCCESSFULLY,
        };
      }
    } else {
      let data, error;

      if (isEnvTrue("REACT_APP_SUPABASE_DEFAULT_PASSWORD")) {
        const res = await supabase.auth.admin.createUser({
          email: email.toLowerCase(),
          password: "Admin@123",
          email_confirm: true,
        });

        if(res.error){
          return { success: false, error: res.error.message || messages.INVITE_SENT_FAILED };
        }
        data = res.data;
        error = res.error;
      } else {
        const res = await supabase.auth.admin.inviteUserByEmail(email, {
          data: { role },
          // redirectTo: `${process.env.REACT_APP_URL}/reset-password`,
        });

        if(res.error){
          return { success: false, error: res.error.message || messages.INVITE_SENT_FAILED };
        }
        data = res.data;
        error = res.error;
      }

      if(userData?.id){
        const { error: updateError } = await supabase
        .from("company_user")
        .update({
          user_id: data.user.id,
        })
        .eq("id", userData.id);

        if (updateError) {
          return { success: false, error: updateError.message || updateError };
        }
        data = data.user.id;
      }
      return {
        success: true,
        userId: data?.user?.id,
        message: messages.INVITE_SENT_SUCCESSFULLY,
      };
    }
  } catch (error) {
    console.error("error", error);
    return { success: false, error: error.message || messages.INVITE_SENT_FAILED };
  }
}

export async function resendPasswordReset(email) {
  try {
    if (!email || typeof email !== "string") {
      return { success: false, error: "Invalid email format" };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { success: false, error: "Invalid email format" };
    }

    // Send password reset email
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      // redirectTo: `${process.env.REACT_APP_URL}/reset-password`,
    });

    if (error) {
      console.error("Password reset error:", error);
      return { success: false, error: error.message };
    }

    return { success: true, message: messages.INVITE_SENT_SUCCESSFULLY };
  } catch (error) {
    console.error("error", error);
    return { success: false, error: messages.INVITE_SENT_FAILED };
  }
}

export function getLocalData(key) {
  return key
    ? JSON.parse(localStorage.getItem(key))
    : JSON.parse(localStorage.getItem("sdfUser"));
}

/** True when department is blank, null, or legacy "none" (no department). Do not save "none" in DB; use null. */
export function isNoDepartment(deptId) {
  if (deptId == null) return true;
  const s = String(deptId).trim().toLowerCase();
  return s === "" || s === "none";
}

/** Normalize no-department value for saving: return null for blank/null/"none". Saves as null in DB. */
export function normalizeDepartmentIdForSave(deptId) {
  return isNoDepartment(deptId) ? null : (typeof deptId === "string" ? deptId.trim() : deptId);
}

export function buildDepartmentHierarchy(rootDepartmentId = null, departments) {
  // Step 1: Create a map of departmentId to department
  const deptMap = new Map();
  departments.forEach((department) => {
    deptMap.set(department.departmentId, {
      ...department,
      sub_departments: [],
    });
  });

  // Step 2: Build the hierarchy
  departments.forEach((department) => {
    if (department.parentDepartmentId) {
      const parentDept = deptMap.get(department.parentDepartmentId);
      if (parentDept) {
        parentDept.sub_departments.push(deptMap.get(department.departmentId));
      }
    }
  });

  // Step 3: Determine roots based on rootDepartmentId
  if (rootDepartmentId) {
    // Return the hierarchy starting from the given rootDepartmentId
    const root = deptMap.get(rootDepartmentId);
    return root ? [root] : [];
  } else {
    // Return all top-level departments (parentDepartmentId === null)
    return departments
      .filter((dept) => dept.parentDepartmentId === null)
      .map((dept) => deptMap.get(dept.departmentId));
  }
}

export const getDepartmentId = (parentDepartmentId,organogramData) => {
  const department = organogramData.find(
    (item) => item?.departmentId === parentDepartmentId
  );
  if (department?.approvalRequired && department.departmentId && department.departmentHeadId) {
    return {
      deptId: department.departmentId,
      deptHeadId: department.departmentHeadId,
    };
  }
  if (department?.parentDepartmentId) {
    return getDepartmentId(department?.parentDepartmentId,organogramData);
  }
  return null;
};

export const exportExcel = (data, fileName) => {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(data);

  XLSX.utils.book_append_sheet(wb, ws, fileName);

  XLSX.writeFile(wb, `${fileName}-${Date.now()}.xlsx`);
};

export const formType = {
  RECOMMENDED: "Recommended",
  REQUEST_EDIT: "Request Edit",
  REQUESTED: "Requested",
  DRAFT: "Draft",
}

export const sendEmailNotification = async (
  receiverEmail,
  notificationType,
  data,

) => {
  try {
    const requestBody = {
      email: receiverEmail,
      notificationType,
      data,
    };

    // // If template data is provided, use HTML template
    // if (templateData) {
    //   requestBody.templateType = typeKey;
    //   requestBody.templateData = templateData;
    // }

    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/send-email-nodemailer`,
      requestBody,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${process.env.REACT_APP_BACKEND_API_KEY}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("error", error);
    return { success: false, error };
  }
};

function getAllocatedTrainingCategory(item) {
  return String(item?.training_category ?? item?.allocated_training_category ?? "").trim();
}

function matchesTrainingCategoryConfig(item, configTrainingType) {
  const itemCategory = getAllocatedTrainingCategory(item);
  const configCategory = String(configTrainingType ?? "").trim();
  if (!itemCategory || !configCategory) return false;
  if (itemCategory === configCategory) return true;
  return (
    itemCategory.replace(/^Category\s+/i, "") ===
    configCategory.replace(/^Category\s+/i, "")
  );
}

export function getTrainingTypeBudgetUsage(trainingTypeConfig, allocatedData, setaFields) {
  const { costField, genderField } = setaFields;

  if (!allocatedData || allocatedData.length === 0) {
    return [];
  }

  // If trainingTypeConfig is not provided, derive training types from allocated_data
  let configToUse = trainingTypeConfig;
  
  if (!trainingTypeConfig || trainingTypeConfig.length === 0) {
    // Extract unique training categories from allocated_data
    const uniqueTrainingCategories = [
      ...new Set(
        allocatedData.map((item) => getAllocatedTrainingCategory(item)).filter(Boolean),
      ),
    ];
    
    // Create a basic config structure for each unique training category
    configToUse = uniqueTrainingCategories.map(category => ({
      trainingType: category,
      budget: 0, // Default budget when no config is provided
      headCount: {
        male: 0,
        female: 0
      }
    }));
  }

  // Separate data for null/undefined training types and valid training types
  const validTrainingTypes = [];
  const otherTrainingData = [];

  // First, collect all data that has null/undefined/empty training type
  const dataWithEmptyTrainingType = allocatedData.filter(
    (item) => !getAllocatedTrainingCategory(item),
  );
  
  if (dataWithEmptyTrainingType.length > 0) {
    otherTrainingData.push(...dataWithEmptyTrainingType);
  }

  // Process each type config for valid training types
  configToUse.forEach(typeConfig => {
    const { budget, headCount, trainingType } = typeConfig;
    
    // Only process if trainingType is not null, undefined, or empty
    if (trainingType && trainingType !== null && trainingType !== undefined && trainingType !== '') {
      // Filter data for valid training types
      const trainingTypeData = allocatedData.filter((item) =>
        matchesTrainingCategoryConfig(item, trainingType),
      );
      
      validTrainingTypes.push({
        typeConfig,
        trainingTypeData
      });
    }
  });

  const results = [];

  // Process all training types from config (including those with no allocated data)
  validTrainingTypes.forEach(({ typeConfig, trainingTypeData }) => {
    const { budget, headCount, trainingType } = typeConfig;
    
    // Calculate recommended budget (Recommended status only)
    const recommendedTrainingsBudget = trainingTypeData
      .filter(item => item.status == trainingFormStatus.RECOMMENDED)
      .reduce((sum, item) => sum + (Number(item[costField]) || 0), 0);

    const approvedTrainingsBudget = trainingTypeData
      .filter(item => item.status == trainingFormStatus.APPROVED)
      .reduce((sum, item) => sum + (Number(item[costField]) || 0), 0);
    
    // Calculate requested budget (Requested status)
    const requestedTrainingsBudget = trainingTypeData
      .filter(item => item.status && item.status == trainingFormStatus.REQUESTED)
      .reduce((sum, item) => sum + (Number(item[costField]) || 0), 0);
    
    // Get total head count from config (separate male and female)
    const totalHeadCount = {
      male: headCount?.male || 0,
      female: headCount?.female || 0
    };
    
    const recommendedItems = trainingTypeData.filter(item => item.status == trainingFormStatus.RECOMMENDED);
    const recommendedMaleCount = recommendedItems.filter(item => item[genderField]?.toLowerCase() === 'male').length;
    const recommendedFemaleCount = recommendedItems.filter(item => item[genderField]?.toLowerCase() === 'female').length;
    const approvedItems = trainingTypeData.filter(item => item.status == trainingFormStatus.APPROVED);
    const approvedMaleCount = approvedItems.filter(item => item[genderField]?.toLowerCase() === 'male').length;
    const approvedFemaleCount = approvedItems.filter(item => item[genderField]?.toLowerCase() === 'female').length;
    // Count requested males and females
    const requestedItems = trainingTypeData.filter(item => item.status && item.status == trainingFormStatus.REQUESTED);
    const requestedMaleCount = requestedItems.filter(item => item[genderField]?.toLowerCase() === 'male').length;
    const requestedFemaleCount = requestedItems.filter(item => item[genderField]?.toLowerCase() === 'female').length;
    
    results.push({
      trainingType,
      totalBudget: Number(budget) || 0,
      recommendedTrainingsBudget,
      approvedTrainingsBudget,
      requestedTrainingsBudget,
      totalHeadCount,
      recommendedCount: {
        male: recommendedMaleCount,
        female: recommendedFemaleCount
      },
      approvedCount: {
        male: approvedMaleCount,
        female: approvedFemaleCount
      },
      requestedCount: {
        male: requestedMaleCount,
        female: requestedFemaleCount
      }
    });
  });

  // Process "Other" category for null/undefined training types
  if (otherTrainingData.length > 0) {
    // Calculate recommended budget (approved status)
    const recommendedTrainingsBudget = otherTrainingData
      .filter(item => item.status == trainingFormStatus.RECOMMENDED)
      .reduce((sum, item) => sum + (Number(item[costField]) || 0), 0);

    const approvedTrainingsBudget = otherTrainingData
      .filter(item => item.status == trainingFormStatus.APPROVED)
      .reduce((sum, item) => sum + (Number(item[costField]) || 0), 0);

    const requestedTrainingsBudget = otherTrainingData
      .filter(item => item.status && item.status == trainingFormStatus.REQUESTED)
      .reduce((sum, item) => sum + (Number(item[costField]) || 0), 0);

    // Count recommended males and females (approved status)
    const recommendedItems = otherTrainingData.filter(item => item.status == trainingFormStatus.RECOMMENDED);
    const recommendedMaleCount = recommendedItems.filter(item => item[genderField]?.toLowerCase() === 'male').length;
    const recommendedFemaleCount = recommendedItems.filter(item => item[genderField]?.toLowerCase() === 'female').length;
    const approvedItems = otherTrainingData.filter(item => item.status == trainingFormStatus.APPROVED);
    const approvedMaleCount = approvedItems.filter(item => item[genderField]?.toLowerCase() === 'male').length;
    const approvedFemaleCount = approvedItems.filter(item => item[genderField]?.toLowerCase() === 'female').length;

    const requestedItems = otherTrainingData.filter(item => item.status && item.status == trainingFormStatus.REQUESTED);
    const requestedMaleCount = requestedItems.filter(item => item[genderField]?.toLowerCase() === 'male').length;
    const requestedFemaleCount = requestedItems.filter(item => item[genderField]?.toLowerCase() === 'female').length;

    results.push({
      trainingType: "Other",
      totalBudget: 0, // No budget allocated for "Other" category
      recommendedTrainingsBudget,
      approvedTrainingsBudget,
      requestedTrainingsBudget,
      totalHeadCount: {
        male: 0,
        female: 0
      },
      recommendedCount: {
        male: recommendedMaleCount,
        female: recommendedFemaleCount
      },
      approvedCount: {
        male: approvedMaleCount,
        female: approvedFemaleCount
      },
      requestedCount: {
        male: requestedMaleCount,
        female: requestedFemaleCount
      }
    });
  }

  return results;
}

/**
 * Get training category budget usage for configured training categories.
 * @param {Array} categoryConfig - Configuration array with category, percentage, budget
 * @param {Array} allocatedData - Allocated training data
 * @param {Object} setaFields - SETA fields configuration
 * @param {Array|null} [employmentTypes] - SETA employment types; when provided, unemployed stipend
 *   is added once per form to that form's first training category (parity with Plan Optimiser /
 *   B-BBEE unemployed C = programme cost + stipend).
 * @returns {Array} Array of category budget usage objects
 */
export function getTrainingCategoryBudgetUsage(
  categoryConfig,
  allocatedData,
  setaFields,
  employmentTypes = null,
) {
  // Handle setaFields as either an object or array
  let trainingCategoryField = 'training_category';
  let costField = 'cost';

  if (Array.isArray(setaFields)) {
    // If setaFields is an array, find the fields
    const categoryFieldObj = setaFields.find(field => 
      field.config?.isTrainingCategoryField || field.name?.toLowerCase().includes('category')
    );
    trainingCategoryField = categoryFieldObj?.name || 'training_category';

    const costFieldObj = setaFields.find(field => 
      field.config?.isCostField || field.name?.toLowerCase().includes('cost')
    );
    costField = costFieldObj?.name || 'cost';
  } else if (setaFields && typeof setaFields === 'object') {
    // If setaFields is an object (like in Dashboard), use direct properties
    // Check if it has a trainingCategory field property
    if (setaFields.trainingCategory) {
      trainingCategoryField = setaFields.trainingCategory;
    } else if (setaFields.trainingCategoryField) {
      trainingCategoryField = setaFields.trainingCategoryField;
    }
    // Default to 'training_category' if not found in object
    
    // Use costField from setaFields object
    if (setaFields.costField) {
      costField = setaFields.costField;
    }
  }

  const data = allocatedData || [];

  // If categoryConfig is not provided, use all categories from TRAINING_CATEGORY_OPTIONS
  let configToUse = categoryConfig;
  
  if (!categoryConfig || categoryConfig.length === 0) {
    configToUse = TRAINING_CATEGORY_OPTIONS.map(cat => ({
      category: cat.value,
      budget: 0
    }));
  }

  const normalizeCategoryKey = (raw) => {
    if (raw == null || raw === "") return null;
    const normalizedName = String(raw).trim();
    return (
      TRAINING_CATEGORY_OPTIONS.find((cat) => {
        const normalizedCatValue = String(cat.value).trim();
        return (
          normalizedName === normalizedCatValue ||
          normalizedName === normalizedCatValue.replace("Category ", "") ||
          normalizedName.replace("Category ", "") === normalizedCatValue
        );
      })?.value ?? normalizedName
    );
  };

  const results = [];

  // Process each category from config
  configToUse.forEach(categoryConfigItem => {
    const { category, budget } = categoryConfigItem;
    const normalizedCategory = normalizeCategoryKey(category) ?? String(category).trim();
    
    // Filter data for this category (match "Category X" or letter-only forms via TRAINING_CATEGORY_OPTIONS)
    const categoryData = data.filter(item => {
      const itemCategory = item[trainingCategoryField];
      if (!itemCategory) return false;
      const normalizedItemCategory = String(itemCategory).trim();
      const normalizedConfigCategory = String(category).trim();
      return normalizedItemCategory === normalizedConfigCategory || 
             normalizedItemCategory === normalizedConfigCategory.replace('Category ', '') ||
             normalizedItemCategory.replace('Category ', '') === normalizedConfigCategory;
    });
    
    const recommendedTrainingsBudget = categoryData
      .filter(item => item.status == trainingFormStatus.RECOMMENDED)
      .reduce((sum, item) => sum + (Number(item[costField]) || 0), 0);

    const approvedTrainingsBudget = categoryData
      .filter(item => item.status == trainingFormStatus.APPROVED)
      .reduce((sum, item) => sum + (Number(item[costField]) || 0), 0);
    
    const requestedTrainingsBudget = categoryData
      .filter(item => item.status && item.status == trainingFormStatus.REQUESTED)
      .reduce((sum, item) => sum + (Number(item[costField]) || 0), 0);
    
    results.push({
      category: normalizedCategory,
      totalBudget: Number(budget) || 0,
      recommendedTrainingsBudget,
      approvedTrainingsBudget,
      requestedTrainingsBudget
    });
  });

  // Unemployed stipend once per form → first training's category (same as Generate Plan / BBEE Unemployed C)
  if (hasUnemployedTypesConfigured(employmentTypes)) {
    /** @type {Map<string, { amount: number, category: string|null, recommended: boolean, approved: boolean, requested: boolean }>} */
    const stipendByForm = new Map();
    data.forEach((item) => {
      if (
        !isUnemployedEmploymentType(employmentTypes, item?.employment_type)
      ) {
        return;
      }
      const formId = item.form_id;
      if (formId == null) return;
      const key = String(formId);
      const status = item.status ?? item.form_status;
      const cat = normalizeCategoryKey(item[trainingCategoryField]);
      const existing = stipendByForm.get(key) ?? {
        amount: 0,
        category: null,
        recommended: false,
        approved: false,
        requested: false,
      };
      if (!existing.amount) {
        existing.amount = Number(item.stipend_amount) || 0;
      }
      // First row with a category wins (stable attribution for this pass)
      if (!existing.category && cat) {
        existing.category = cat;
      }
      if (status === trainingFormStatus.RECOMMENDED) existing.recommended = true;
      else if (status === trainingFormStatus.APPROVED) existing.approved = true;
      else if (status === trainingFormStatus.REQUESTED) existing.requested = true;
      stipendByForm.set(key, existing);
    });

    stipendByForm.forEach(({ amount, category, recommended, approved, requested }) => {
      if (!amount || !category) return;
      const bucket = results.find((r) => r.category === category);
      if (!bucket) return;
      // Prefer planned statuses (same priority as unemployed B-BBEE amounts)
      if (approved) bucket.approvedTrainingsBudget += amount;
      else if (recommended) bucket.recommendedTrainingsBudget += amount;
      else if (requested) bucket.requestedTrainingsBudget += amount;
    });
  }

  return results;
}

// Date/time utilities have been moved to datetimeUtils.js
// Import from: import { getEndOfDayFromISOString, ... } from "../../constant/utils/datetimeUtils";

/**
 * Formats a given value as a South African Rand (ZAR) currency string using the 'en-ZA' locale.
 * Ensures the output uses the "R" symbol, two decimal places, and a space as the thousands separator.
 * If the input is not a valid number, returns 'R0,00'.
 *
 * @param {number} value - The numeric value to format as ZAR currency.
 * @returns {string} The formatted currency string (e.g., "R1 234,56").
 */
/**
 * Column definitions for Selected Trainings in form history compare (SETA-driven).
 */
export function getFormHistoryTrainingColumns(
  setaTemplate = [],
  rows = [],
  compareRows = []
) {
  const fields = Array.isArray(setaTemplate) ? setaTemplate : [];
  const allRows = [...rows, ...compareRows];

  const cols = [
    { key: "name", header: "Training Name" },
    { key: "program_duration", header: "Program Duration" },
  ];

  const categoryField = fields.find((f) => f?.config?.isTrainingCategoryField);
  cols.push({
    key: "allocated_training_category",
    header: categoryField?.fieldName || "Training Category",
  });

  const typeField = fields.find((f) => f?.config?.isTrainingTypeField);
  cols.push({
    key: "allocated_training_type",
    header: typeField?.fieldName || "Training Type",
  });

  const costField = fields.find((f) => f?.config?.isTrainingCostField);
  cols.push({
    key: "cost",
    header: costField?.fieldName || "Cost per Learner",
    isCost: true,
  });

  const templateAdditional = fields.filter((f) => f?.config?.isAdditionalField);
  const coveredNames = new Set();

  templateAdditional.forEach((af) => {
    coveredNames.add(af.fieldName);
    cols.push({
      key: `additional:${af.fieldName}`,
      header: af.fieldName,
      additionalField: af,
      getValue: (row) => row.additionalFields?.[af.fieldName],
    });
  });

  // Include allocation keys present in snapshot data but not in SETA template
  const discovered = new Set();
  allRows.forEach((row) => {
    const extra = row?.additionalFields;
    if (!extra || typeof extra !== "object") return;
    Object.keys(extra).forEach((name) => {
      if (!coveredNames.has(name)) discovered.add(name);
    });
  });
  discovered.forEach((fieldName) => {
    cols.push({
      key: `additional:${fieldName}`,
      header: fieldName,
      getValue: (row) => row.additionalFields?.[fieldName],
    });
  });

  return cols;
}

export function formatZAR(value) {
  if (typeof value !== 'number' || isNaN(value)) return 'R 0,00';

  const formatted = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

  // Replace non-breaking space with normal space
  return formatted.replace('\u00A0', ' ');
}

export const SearchQueryMatch = (value, lowerCaseSearchQuery) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
     const label = trainingFormStatusLabels
     [value];
    if (label) {
      return label.toLowerCase().includes(lowerCaseSearchQuery);
    }
    return value?.toString()?.toLowerCase()?.includes(lowerCaseSearchQuery);
  }

  // If it's an array, recursively check any of its items
  if (Array.isArray(value)) {
    return value.some(item => SearchQueryMatch(item, lowerCaseSearchQuery));
  }

  // If it's an object, recursively check any of its property values
  if (typeof value === 'object') {
    return Object.values(value).some(nestedValue => SearchQueryMatch(nestedValue, lowerCaseSearchQuery));
  }

  return false;
};

// Helper function to convert column number to Excel column letter
const getExcelColumnLetter = (colNumber) => {
  let columnLetter = '';
  while (colNumber > 0) {
    const modulo = (colNumber - 1) % 26;
    columnLetter = String.fromCharCode(65 + modulo) + columnLetter;
    colNumber = Math.floor((colNumber - modulo) / 26);
  }
  return columnLetter;
};

export const downloadExcelTemplateUtility = async ({
  fileName,
  sheetName,
  columns,
  /** @type {string[][]|undefined} rows of cell values, same column order as headers; shown below the header row */
  sampleRows = undefined,
  dropdownConfigs = [],
  getHeader = (col, context) => col.headerName,
  context = {},
  dataValidationStartRow = 2,
  dataValidationEndRow = 100,
}) => {
  
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);
  const validationSheet = workbook.addWorksheet('Validation');
  
  // Hide validation sheet
  validationSheet.state = 'hidden';

  // Build headers based on conditional logic
  const headers = columns?.map((col) => {
    const header = getHeader(col, context);
    return header ? String(header).trim() : "";
  });
  worksheet.addRow(headers);

  if (Array.isArray(sampleRows) && sampleRows.length > 0) {
    sampleRows.forEach((row) => {
      if (Array.isArray(row) && row.length) {
        worksheet.addRow(row);
      }
    });
  }

  // Create validation lists in hidden sheet
  let validationRow = 1;
  // Use Map with columnIndex as key for accurate mapping
  const validationRangesByIndex = new Map();
  // Also keep a name-based map as fallback
  const validationRangesByName = new Map();

  if(Array.isArray(dropdownConfigs) && dropdownConfigs.length > 0) {
    dropdownConfigs.forEach(({ columnIndex, columnName, options, allowBlank }) => {
      if (Array.isArray(options) && options.length > 0) {
        const sanitizedOptions = options
          .map(opt => String(opt).trim())
          .filter(opt => opt.length > 0);

        if (sanitizedOptions.length > 0) {
          // Add options starting from column A, each dropdown in its own row
          // This ensures each dropdown's options are in a separate row to avoid overlap
          sanitizedOptions.forEach((opt, index) => {
            validationSheet.getCell(validationRow, index + 1).value = opt;
          });

          // Calculate range using proper Excel column letters
          const endColIndex = sanitizedOptions.length;
          const endColLetter = getExcelColumnLetter(endColIndex);
          const range = `Validation!$A$${validationRow}:$${endColLetter}$${validationRow}`;
          
          const validationConfig = {
            range: range,
            allowBlank: allowBlank !== false // Default to true if not specified
          };
          
          // Store by column index (most accurate)
          if (columnIndex !== undefined && columnIndex !== null) {
            validationRangesByIndex.set(columnIndex, validationConfig);
          }
          
          // Also store by normalized column name as fallback
          const normalizedColumnName = String(columnName).trim().toLowerCase();
          validationRangesByName.set(normalizedColumnName, validationConfig);
          
          validationRow++;
        }
      }
    });
  }

  // Apply dropdown validations to columns
  headers.forEach((header, colIndex) => {
    // First try to match by column index (most accurate)
    let validationConfig = validationRangesByIndex.get(colIndex);
    
    // If not found by index, try matching by name (fallback)
    if (!validationConfig) {
      const normalizedHeader = String(header).trim().toLowerCase();
      validationConfig = validationRangesByName.get(normalizedHeader);
    }
    
    if (validationConfig) {
      for (let rowIndex = dataValidationStartRow; rowIndex <= dataValidationEndRow; rowIndex++) {
        worksheet.getCell(rowIndex, colIndex + 1).dataValidation = {
          type: 'list',
          allowBlank: validationConfig.allowBlank,
          formulae: [validationConfig.range],
          showErrorMessage: true,
          errorStyle: 'information',
        };
      }
      worksheet.getColumn(colIndex + 1).width = 20;
    }
  });
  try {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, fileName);
  } catch (error) {
    console.error("error", error);
  }
};

/**
 * Helper function to normalize gender value to "male", "female", or "other"
 * Handles formats like: "M - Male", "F - Female", "Male", "Female", "M", "F", etc.
 * Returns "male", "female", or "other" (empty values return "other")
 * @param {any} genderValue - The gender value to normalize
 * @returns {string} - "male", "female", or "other"
 */
export function normalizeGender(genderValue) {
  if (genderValue === undefined || genderValue === null || genderValue === "") {
    return 'other';
  }
  
  const genderStr = String(genderValue).toLowerCase().trim();
  
  // Check for "female" FIRST (more specific, contains "male" as substring)
  // Handles: Female, female, F, f, F-Female, etc.
  if (genderStr.includes('female') || (genderStr.startsWith('f') && !genderStr.includes('male'))) {
    return 'female';
  }
  
  // Check if it contains "male" or starts with "m" (handles: Male, male, M, m, M-Male, etc.)
  // Note: This comes after female check to avoid matching "female"
  if (genderStr.includes('male') || genderStr.startsWith('m')) {
    return 'male';
  }
  
  // Any other value → "other"
  return 'other';
}